import { List } from "immutable";
import React, { PropsWithChildren, RefObject, useCallback, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router";
import styled from "styled-components";
import { useRegisterState } from "@domain/currentProfile/hooks";
import { RegisterState } from "@domain/currentProfile/registerState";
import { useChangeEffect } from "@utils/hooks";
import { Z } from "@ui/styles/zIndex";
import { ModalConfig, ModalContext, ModalRenderer } from "./modalContext";

interface ModalInfo {
	renderer: ModalRenderer;
	config: ModalConfig;
}

export const ModalContainer: React.FC<PropsWithChildren> = ({ children }) => {
	const [modals, setModals] = React.useState(List<ModalInfo>());

	const createModal = useCallback(
		(
			renderer: ModalRenderer,
			config: ModalConfig = {
				closeOnBackdropClick: false,
				closeOnLocationChange: true,
			}
		): void => {
			setModals((c) => c.push({ renderer, config }));
		},
		[]
	);

	const closeModal = useCallback((): void => {
		setModals((c) => c.pop());
	}, []);

	const backdropRef = useRef<HTMLDivElement>(null);
	const registerState = useRegisterState();
	const skipInitialLogout = useRef(true);

	useLayoutEffect(() => {
		if (skipInitialLogout.current) {
			skipInitialLogout.current = false;
			return;
		}
		if (registerState === RegisterState.LOGOUT) {
			setModals((current) => (current.isEmpty() ? current : current.clear()));
		}
	}, [registerState]);

	return (
		<ModalContext.Provider value={{ createModal, backdropRef, activeModalsCount: modals.size }}>
			{children}
			{modals.map((modalInfo, i) => (
				<ModalWrapper modalInfo={modalInfo} key={i} backdropRef={backdropRef} closeModal={closeModal} />
			))}
		</ModalContext.Provider>
	);
};

interface ModalWrapperProps {
	modalInfo: ModalInfo;
	backdropRef: RefObject<HTMLDivElement | null>;
	closeModal: () => void;
}
export const ModalWrapper: React.FC<ModalWrapperProps> = ({ modalInfo, backdropRef, closeModal }) => {
	const location = useLocation();

	useChangeEffect(() => {
		closeModal();
	}, [location.pathname, location.search, closeModal]);
	return (
		<BackDrop
			className="modal-backdrop"
			ref={backdropRef}
			onClick={() => {
				if (document.getSelection()?.isCollapsed) {
					modalInfo.config?.onBackdropClick?.();
					if (modalInfo.config?.closeOnBackdropClick) {
						closeModal();
					}
				}
			}}
			backdropOpacity={0.5}
		>
			<div>{modalInfo.renderer(closeModal)}</div>
		</BackDrop>
	);
};

const BackDrop = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "backdropOpacity",
})<{ backdropOpacity?: number }>`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	background-color: rgba(0, 0, 0, ${({ backdropOpacity }) => backdropOpacity || 0});
	display: flex;
	flex-direction: column;
	align-items: stretch;
	padding: 20px;
	@media (max-width: 900px) {
		padding: 20px 20px 60px;
	}
	z-index: ${Z.modal};
	overflow-y: scroll;

	> div  {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;

		@media not all and (min-resolution: 0.001dpcm) {
			@supports (-webkit-appearance: none) {
				/* Fix safari bugs */
				flex-direction: row;
				justify-content: center;
				align-items: flex-start;
			}
		}

		margin-top: 20px;
		margin-bottom: 20px;
	}
`;
